import { estadoDelPrestamo } from './prestamo';

// La simulación de un crédito, para contestar en el momento.
//
// «Préstame 500.000 al 10% y te los pago en dos meses, ¿cuánto te doy
// semanal?». Esa cuenta se hacía de cabeza o en la calculadora del teléfono, y
// la de cabeza sale mal en cuanto el cliente abona a capital: el interés del
// segundo mes ya no es el mismo.
//
// Aquí NO se hace una cuenta aparte. Se simula el préstamo de verdad, con el
// mismo motor que lleva los préstamos vivos, y se busca la cuota que lo deja en
// cero. Así lo que se le promete al cliente en la calle es exactamente lo que
// el programa le va a cobrar después. Una fórmula propia acabaría dando otro
// número, y ahí empiezan los reclamos.

// Hay DOS frecuencias en juego y no son la misma:
//   · cada cuánto se genera el interés  —el 10% mensual del prestamista—
//   · cada cuánto entrega plata el cliente —semanal, diario…—
// Confundirlas es lo que hace que las cuentas de cabeza no cuadren.
const DIAS = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };

export const NOMBRE_FRECUENCIA = {
  daily: 'diario', weekly: 'semanal', biweekly: 'quincenal', monthly: 'mensual',
};
export const NOMBRE_CUOTA = {
  daily: 'diaria', weekly: 'semanal', biweekly: 'quincenal', monthly: 'mensual',
};

const unDia = 86400000;

// La fecha de vencimiento a partir del plazo que se teclee.
export function fechaDeVencimiento(inicio, cantidad, unidad) {
  const d = new Date(inicio);
  const n = Math.max(1, Math.round(Number(cantidad) || 0));
  if (unidad === 'meses') {
    // Por mes de calendario, igual que los cortes: si se presta el 15 de enero,
    // dos meses son el 15 de marzo. Y con tope al último día, para no pedir un
    // 31 de febrero.
    const a = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const dia = d.getUTCDate();
    const am = a + Math.floor((m + n) / 12);
    const mm = (m + n) % 12;
    const ultimo = new Date(Date.UTC(am, mm + 1, 0)).getUTCDate();
    return new Date(Date.UTC(am, mm, Math.min(dia, ultimo)));
  }
  const porDia = unidad === 'semanas' ? 7 : 1;
  return new Date(d.getTime() + n * porDia * unDia);
}

// Los días en que el cliente va a entregar plata, desde el primero hasta el
// vencimiento. El del vencimiento entra: es el último cobro.
export function fechasDePago(inicio, frecuenciaPago, vencimiento) {
  const paso = DIAS[frecuenciaPago] || 30;
  const fechas = [];
  if (frecuenciaPago === 'monthly') {
    // Los mensuales caen el mismo día de cada mes, como los cortes.
    let n = 1;
    for (;;) {
      const f = fechaDeVencimiento(inicio, n, 'meses');
      if (f.getTime() > vencimiento.getTime()) break;
      fechas.push(f);
      n++;
      if (n > 600) break;
    }
    return fechas;
  }
  for (let t = inicio.getTime() + paso * unDia;
       t <= vencimiento.getTime() + 1000;
       t += paso * unDia) {
    fechas.push(new Date(t));
    if (fechas.length > 4000) break;
  }
  return fechas;
}

// Cuánto queda debiendo si paga `cuota` en cada una de esas fechas.
function loQueQueda(prestamo, fechas, cuota, vencimiento) {
  const pagos = fechas.map((f, i) => ({
    id: i + 1, payment_date: f.toISOString(), amount: cuota,
  }));
  return estadoDelPrestamo(prestamo, pagos, vencimiento).deudaTotal;
}

// Busca la cuota que deja el préstamo en cero justo al vencer.
//
// Se busca partiendo por la mitad una y otra vez en vez de despejar una
// fórmula: la fórmula habría que rehacerla cada vez que cambie una regla del
// dinero —los días de gracia, el mes consumido— y tarde o temprano una de las
// dos se quedaría vieja. Así la simulación no puede desalinearse del programa,
// porque ES el programa.
export function simularCredito({
  monto, tasa, frecuenciaInteres = 'monthly', frecuenciaPago = 'weekly',
  plazo = 2, unidadPlazo = 'meses', inicio = new Date(),
}) {
  const capital = Math.round(Number(monto) || 0);
  const porcentaje = Number(tasa) || 0;
  if (capital <= 0 || porcentaje < 0) return null;

  // La fecha de arranque se lleva a medianoche universal, como se guardan las
  // de los préstamos: si no, la simulación y el préstamo creado no coinciden.
  const arranque = new Date(Date.UTC(
    inicio.getFullYear(), inicio.getMonth(), inicio.getDate()));
  const vencimiento = fechaDeVencimiento(arranque, plazo, unidadPlazo);
  const fechas = fechasDePago(arranque, frecuenciaPago, vencimiento);
  if (fechas.length === 0) return null;

  const prestamo = {
    amount: capital,
    interest_rate: porcentaje,
    frequency: frecuenciaInteres,
    start_date: arranque.toISOString(),
    maturity_date: vencimiento.toISOString(),
    liquidacion: 'mensual',
  };

  let bajo = 0;
  let alto = capital * 5 + 1;
  for (let i = 0; i < 60; i++) {
    const medio = (bajo + alto) / 2;
    if (loQueQueda(prestamo, fechas, medio, vencimiento) > 0) bajo = medio;
    else alto = medio;
  }
  const cuota = alto;

  // Nadie cobra $71.176 en la calle. Se sugiere el múltiplo de mil de arriba y
  // se dice qué pasa con la diferencia, que siempre juega a favor del cliente:
  // paga un poco más cada vez y le sobra al final.
  const redondeada = Math.ceil(cuota / 1000) * 1000;

  const conRedondeo = fechas.map((f, i) => ({
    id: i + 1, payment_date: f.toISOString(), amount: redondeada,
  }));
  const estado = estadoDelPrestamo(prestamo, conRedondeo, vencimiento);

  // Lo que le sobra por el redondeo se saca de ESTA simulación, la de la cuota
  // que se va a cobrar de verdad. Antes se sacaba del interés de la cuota
  // exacta, que es otro: con la redondeada el capital baja un poco más rápido y
  // el interés de los meses siguientes es menor. Salía por unos cientos de
  // pesos, en un renglón que el cliente puede sumar con el dedo.
  const sobra = Math.max(0, Math.round(
    redondeada * fechas.length - (capital + estado.interesGenerado)));

  return {
    capital,
    tasa: porcentaje,
    // Cada cuota diciendo cuánto de ella va al interés y cuánto a la deuda
    reparto: estado.liquidaciones.map(repartoPorCuota),
    frecuenciaInteres,
    frecuenciaPago,
    vencimiento,
    fechas,
    cuotas: fechas.length,
    cuotaExacta: Math.round(cuota),
    cuota: redondeada,
    totalAPagar: redondeada * fechas.length,
    ganancia: redondeada * fechas.length - capital,
    // Lo que le sobraría de la última cuota por haber redondeado hacia arriba.
    sobraAlFinal: sobra,
    liquidaciones: estado.liquidaciones || [],
  };
}

// Cómo se reparte cada cuota dentro de su mes.
//
// En el corte el reparto se hace sobre TODO lo acumulado del mes, no cuota por
// cuota: si en el mes entraron cinco de 37.000, los 185.000 juntos cubren
// primero el interés y el resto baja la deuda. Aquí eso mismo se cuenta cuota a
// cuota, en orden de fecha —las primeras van cubriendo el interés y de ahí en
// adelante todo baja la deuda—, que es como se explica de viva voz y como lo
// haría cualquiera con lápiz.
//
// Las sumas dan exactamente las del corte: no es otro reparto, es el mismo
// visto por dentro.
function repartoPorCuota(liq) {
  let porCubrir = liq.interesDelMes + liq.arrastreAnterior;
  return {
    corte: liq.corte,
    interesDelMes: liq.interesDelMes,
    arrastreAnterior: liq.arrastreAnterior,
    acumulado: liq.acumulado,
    aInteres: liq.aInteres,
    aCapital: liq.aCapital,
    saldoInicial: liq.saldoInicial,
    saldoFinal: liq.saldoFinal,
    cuotas: liq.abonos.map(a => {
      const aInteres = Math.min(a.monto, Math.max(0, porCubrir));
      porCubrir -= aInteres;
      return { fecha: a.fecha, monto: a.monto, aInteres, aCapital: a.monto - aInteres };
    }),
  };
}

// ── La simulación del cobradiario ──
//
// Es la pregunta al revés de la de arriba. En el modelo de Lenin el interés se
// recalcula sobre lo que queda debiendo, así que la cuota hay que buscarla a
// tientas hasta que el saldo dé cero: por eso la otra función tantea.
//
// Aquí no. El interés no se recalcula sobre el saldo: se saca del capital y el
// plazo, se suma, y se divide. Es una división, y tantear sería fingir una
// dificultad que no existe.
//
// ── LA TASA ES POR PERÍODO, NO DEL PRÉSTAMO ENTERO ──
//
// «Le presté un millón al 8% y me lo paga quincenal a 8 meses»: el 8% es
// MENSUAL. Los intereses son los de ocho meses —640.000— más el capital, y eso
// es lo que se divide.
//
// Antes esta función leía el 8 como el interés del préstamo completo, así que
// para dos meses había que teclear 16 y para ocho, 64. Nadie tiene por qué
// multiplicar de cabeza para hablarle a un programa, y el día que a alguien se
// le olvide, el cliente se lleva un préstamo a mitad de precio.
//
// El motor de los préstamos vivos SÍ trabaja con el porcentaje del préstamo
// entero. Por eso aquí se traduce —`tasaTotal`— y con ese número se le
// pregunta. Así la simulación sigue siendo el mismo cálculo que va a cobrar el
// programa, que es de lo que responde la prueba de más abajo.
//
// El redondeo va hacia arriba al millar, como en la otra, pero aquí la
// diferencia NO se le regala al cliente: el total pactado es sagrado en este
// modelo —se dijo el primer día—, así que la última cuota se ajusta y sale más
// pequeña. Es lo que pasa de verdad en la calle: las 29 primeras iguales y la
// última lo que falte.
// Cuántas veces se cobra el interés en el plazo.
//
// Cuando el plazo y el interés hablan de lo mismo —«8% mensual a 8 meses»— la
// respuesta es el número que se pactó, tal cual. Pasarlo por días daría 8,1
// porque los meses del calendario no miden 30, y ese decimal le cobraría 8.000
// pesos de más a alguien que pactó ocho meses redondos.
export function periodosDeInteres({
  plazo, unidadPlazo = 'meses', frecuenciaInteres = 'monthly', inicio, vencimiento,
}) {
  const n = Math.max(1, Math.round(Number(plazo) || 0));
  const iguales = (unidadPlazo === 'meses' && frecuenciaInteres === 'monthly')
    || (unidadPlazo === 'semanas' && frecuenciaInteres === 'weekly')
    || (unidadPlazo === 'dias' && frecuenciaInteres === 'daily');
  if (iguales) return n;
  const dias = Math.round((vencimiento.getTime() - inicio.getTime()) / unDia);
  return dias / (DIAS[frecuenciaInteres] || 30);
}

export function simularCuotaFija({
  monto, tasa, plazo = 2, unidadPlazo = 'meses',
  frecuenciaInteres = 'monthly', frecuenciaPago = 'biweekly', inicio = new Date(),
}) {
  const capital = Math.round(Number(monto) || 0);
  const porcentaje = Number(tasa) || 0;
  if (capital <= 0 || porcentaje < 0 || !(Number(plazo) > 0)) return null;

  const arranqueUTC = new Date(Date.UTC(
    inicio.getFullYear(), inicio.getMonth(), inicio.getDate()));
  const vencimiento = fechaDeVencimiento(arranqueUTC, plazo, unidadPlazo);
  const periodos = periodosDeInteres({
    plazo, unidadPlazo, frecuenciaInteres, inicio: arranqueUTC, vencimiento });

  // El interés de TODO el plazo, que es lo que se pacta el primer día. Y en
  // porcentaje del capital, porque así es como lo guarda el préstamo: el 8%
  // mensual a 8 meses se guarda como un 64%.
  const tasaTotal = Math.round(porcentaje * periodos * 1e6) / 1e6;
  const interesTotal = Math.round(capital * (tasaTotal / 100));
  const total = capital + interesTotal;

  // Las fechas mandan cuántas cuotas son: quincenal a 8 meses son 16, y eso no
  // se teclea — sale del plazo. Preguntar las dos cosas deja teclear un plazo
  // y un número de cuotas que no se corresponden.
  const fechas = fechasDePago(arranqueUTC, frecuenciaPago, vencimiento);
  const n = fechas.length;
  if (n <= 0) return null;

  const exacta = total / n;
  const redondeada = Math.ceil(exacta / 1000) * 1000;

  // Las primeras van iguales; la última se lleva lo que falte para que la suma
  // dé exactamente el total pactado, ni un peso más.
  const ultima = total - redondeada * (n - 1);

  // Cada cuota lleva su parte de capital y su parte de interés en la misma
  // proporción que el préstamo, igual que lo hace el motor. No se cobra
  // «primero el interés»: aquí no hay interés que se acumule mes a mes.
  const parteCapital = total > 0 ? capital / total : 1;
  let saldo = total;
  const reparto = fechas.map((fecha, i) => {
    const monto = i === n - 1 ? ultima : redondeada;
    const aCapital = Math.round(monto * parteCapital);
    saldo = Math.max(0, saldo - monto);
    return {
      numero: i + 1, fecha, monto,
      aCapital, aInteres: monto - aCapital,
      saldo: Math.round(saldo),
    };
  });

  return {
    modalidad: 'cuota_fija',
    capital,
    // La que se tecleó —el 8% mensual— y la del préstamo entero —el 64%—.
    // La segunda va a la pantalla porque es la que hay que escribir al CREAR
    // el préstamo: esa pantalla todavía pide el porcentaje total.
    tasa: porcentaje,
    tasaTotal,
    periodos,
    frecuenciaInteres,
    plazo: Math.max(1, Math.round(Number(plazo) || 0)),
    unidadPlazo,
    frecuenciaPago,
    cuotas: n,
    cuotaExacta: Math.round(exacta),
    cuota: redondeada,
    // Cuánto se lleva la última, que casi nunca es igual a las demás.
    ultimaCuota: ultima,
    ultimaEsDistinta: ultima !== redondeada,
    totalAPagar: total,
    ganancia: interesTotal,
    vencimiento: fechas[n - 1],
    fechas,
    reparto,
  };
}
